/**
 * TaskBoardFilters Component
 *
 * Modern filter interface with grouped layout:
 * - Primary filters (Assignee, Status, Due Date) in top row
 * - Secondary filters grouped by category below
 * - All filters visible by default (no progressive disclosure)
 * - Visual grouping improves scannability
 *
 * Hierarchy: Primary Filters > Secondary Filters (grouped) > Options
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Select,
  Button,
  Checkbox,
  DatePicker,
} from 'antd';
import {
  CloseOutlined,
  SaveOutlined,
  CloudDownloadOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import moment from 'moment';
import {
  taskTypes,
  subTypes,
  statusTypes,
  advertiserTypes,
  urgencyTypes,
  urgencyHexColors,
} from '../../configs/task.config';
import { isRestrictedTaskLevel } from '../../utils/task.utils';
import { space } from '../../configs/designTokens';
import { STATUS_CONFIG } from '../../utils/taskTableUtils';
import '../../styles/index.scss';

const { Option, OptGroup } = Select;

const TaskBoardFilters = ({
  windowWidth,
  showFilters,
  setShowFilters,
  searchParams,
  setSearchParams,
  level,
  showMyTasks,
  isKanban,
  // Filter handlers
  handleAssigneeFilter,
  handleTeamsFilter,
  handleRequestorFilter,
  handleTaskTypeFilter,
  handleTaskSubTypeFilter,
  handleStatusFilter,
  handleAdvertiserTypeFilter,
  handleChannelPartnerFilter,
  handleAdvertiserFilter,
  handleUrgencyFilter,
  handleDueDateFilter,
  // User/Org data
  uniqueUsers,
  uniqueTeams,
  uniqueRequestors,
  channelPartnerOptions,
  advertiserOptions,
  // Loading states
  basicUsersDetailsLoading,
  teamsLoading,
  requestorLoading,
  channelPartnerLoading,
  advertiserLoading,
  // Fetch functions
  fetchBasicUsers,
  fetchBasicUsersRequestor,
  fetchAdvertiserOrgs,
  // Filter actions
  visibleColumns,
  setVisibleColumns,
  projectFilter,
  setProjectFilter,
  onClearFilters,
  onSaveFilters,
  onLoadFilters,
  hasActiveFilters,
  onPaginationReset,
}) => {
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 992;
  const isPlatform = level === 'PLATFORM';

  // Render compact filter select
  const renderFilterSelect = ({
    placeholder,
    value,
    onChange,
    options,
    loading,
    onSearch,
    showSearch = false,
    mode = 'multiple',
  }) => (
    <Select
      className="task-filter-select"
      dropdownClassName="task-filter-dropdown"
      mode={mode}
      showSearch={showSearch}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      loading={loading}
      onSearch={onSearch}
      optionLabelProp="label"
      allowClear
      filterOption={(input, option) => {
        if (typeof option?.label === 'string') return option.label.toLowerCase().includes(input.toLowerCase());
        if (typeof option?.children === 'string') return option.children.toLowerCase().includes(input.toLowerCase());
        return false;
      }}
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 200 }}
    >
      {options}
    </Select>
  );

  return (
    <div className="task-board-filters-modern">
      {/* Filter Panel - Progressive Disclosure */}
      {showFilters && (
        <div className="task-filters__container">
          {/* Primary Filters Row - Always visible (Assignee, Status, Due) */}
          <div className="task-filters__primary">
            <div className="task-filters__primary-row">
              {/* Teams */}
              <div className="task-filters__primary-item">
                {renderFilterSelect({
                  placeholder: 'Teams',
                  value: searchParams.teams ? searchParams.teams.split(',') : [],
                  onChange: handleTeamsFilter,
                  loading: teamsLoading,
                  showSearch: true,
                  options: [...uniqueTeams]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((team) => (
                      <Option key={team.id} value={team.id} label={team.name}>
                        {team.name}
                      </Option>
                    )),
                })}
              </div>

              {/* Assignee */}
              <div className="task-filters__primary-item">
                {renderFilterSelect({
                  placeholder: 'Assignee',
                  value: searchParams.assigneeNames ? searchParams.assigneeNames.split(',') : [],
                  onChange: handleAssigneeFilter,
                  loading: basicUsersDetailsLoading,
                  onSearch: fetchBasicUsers,
                  showSearch: true,
                  options: [...uniqueUsers].sort((a, b) => (a.name || a.username || '').localeCompare(b.name || b.username || '')).map((user) => (
                    <Option key={user.id} value={user.id} label={startCase(user.name || user.username)}>
                      {startCase(user.name || user.username)}
                    </Option>
                  )),
                })}
              </div>

              {/* Status */}
              <div className="task-filters__primary-item">
                <Select
                  className="task-filter-select"
                  dropdownClassName="task-filter-dropdown"
                  mode="multiple"
                  placeholder="Status"
                  value={searchParams.status ? searchParams.status.split(',') : []}
                  onChange={handleStatusFilter}
                  optionLabelProp="label"
                  allowClear
                  filterOption={(input, option) => {
                    if (typeof option?.label === 'string') return option.label.toLowerCase().includes(input.toLowerCase());
                    return false;
                  }}
                  dropdownMatchSelectWidth={false}
                  dropdownStyle={{ minWidth: 200 }}
                  tagRender={({ label, value, closable, onClose }) => {
                    const statusColor = STATUS_CONFIG[value]?.color || '#94a3b8';
                    return (
                      <span
                        className="task-filter-status-tag"
                        style={{
                          background: '#fff',
                          color: statusColor,
                          border: `1px solid ${statusColor}`,
                          borderRadius: 4,
                          padding: '0 7px',
                          marginRight: 4,
                          fontSize: 12,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          lineHeight: '22px',
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: statusColor,
                            flexShrink: 0,
                          }}
                        />
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{ cursor: 'pointer', marginLeft: 2 }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                >
                  {Object.keys(statusTypes).map((status) => {
                    const statusValue = statusTypes[status];
                    const statusColor = STATUS_CONFIG[statusValue]?.color || '#94a3b8';
                    return (
                      <Option key={status} value={statusValue} label={status}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: statusColor,
                              flexShrink: 0,
                            }}
                          />
                          {status}
                        </span>
                      </Option>
                    );
                  })}
                </Select>
              </div>

              {/* Due Date */}
              <div className="task-filters__primary-item task-filters__primary-item--date">
                <DatePicker.RangePicker
                  className="task-filter-datepicker"
                  placeholder={isMobile ? ['From', 'To'] : ['Due From', 'Due To']}
                  value={
                    searchParams.dueDate && searchParams.dueDate.split(',').length === 2
                      ? [
                          moment(searchParams.dueDate.split(',')[0], 'YYYY-MM-DD'),
                          moment(searchParams.dueDate.split(',')[1], 'YYYY-MM-DD'),
                        ]
                      : []
                  }
                  onChange={(dates) =>
                    handleDueDateFilter(
                      dates && dates.length === 2
                        ? dates.map((d) => d.format('YYYY-MM-DD')).join(',')
                        : ''
                    )
                  }
                  allowClear
                  format="MMM D"
                  suffixIcon={<CalendarOutlined />}
                  popupStyle={{
                    maxWidth: isMobile ? '90vw' : 'auto',
                    left: isMobile ? '50%' : undefined,
                    transform: isMobile ? 'translateX(-50%)' : undefined,
                  }}
                  dropdownClassName={isMobile ? 'task-mobile-range-picker-dropdown' : ''}
                  getPopupContainer={isMobile ? (trigger) => trigger.parentElement : undefined}
                />
              </div>

              {/* Close - desktop only */}
              {!isMobile && (
                <button
                  className="task-filters__close-btn"
                  onClick={() => setShowFilters(false)}
                  aria-label="Close filters"
                >
                  <CloseOutlined />
                </button>
              )}
            </div>
          </div>

          <div className="task-filters__secondary">
              {/* People Group */}
              <div className="task-filters__group">
                <span className="task-filters__group-label">People</span>
                <div className="task-filters__group-items">
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Requestor',
                      value: searchParams.requestor ? searchParams.requestor.split(',') : [],
                      onChange: handleRequestorFilter,
                      loading: requestorLoading,
                      onSearch: fetchBasicUsersRequestor,
                      showSearch: true,
                      options: [...uniqueRequestors].sort((a, b) => (a.name || a.username || '').localeCompare(b.name || b.username || '')).map((user) => (
                        <Option key={user.id} value={user.id} label={startCase(user.name || user.username)}>
                          {startCase(user.name || user.username)}
                        </Option>
                      )),
                    })}
                  </div>
                </div>
              </div>

              {/* Type Group */}
              <div className="task-filters__group">
                <span className="task-filters__group-label">Type</span>
                <div className="task-filters__group-items">
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Type',
                      value: searchParams.requestTypes ? searchParams.requestTypes.split(',') : [],
                      onChange: handleTaskTypeFilter,
                      options: taskTypes.map((type) => (
                        <Option key={type} value={type} label={type}>
                          {type}
                        </Option>
                      )),
                    })}
                  </div>
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Subtype',
                      value: searchParams.requestSubTypes ? searchParams.requestSubTypes.split(',') : [],
                      onChange: handleTaskSubTypeFilter,
                      options: Object.entries(subTypes).map(([mainType, subTypeArray]) => (
                        <OptGroup label={mainType} key={mainType}>
                          {subTypeArray.map((subType) => (
                            <Option key={`${mainType}-${subType}`} value={subType} label={subType}>
                              {subType}
                            </Option>
                          ))}
                        </OptGroup>
                      )),
                    })}
                  </div>
                  <div className="task-filters__group-item">
                    <Select
                      className="task-filter-select"
                      dropdownClassName="task-filter-dropdown"
                      mode="multiple"
                      placeholder="Urgency"
                      value={searchParams.urgency ? searchParams.urgency.split(',') : []}
                      onChange={handleUrgencyFilter}
                      optionLabelProp="label"
                      allowClear
                      filterOption={(input, option) => {
                        if (typeof option?.label === 'string') return option.label.toLowerCase().includes(input.toLowerCase());
                        return false;
                      }}
                      dropdownMatchSelectWidth={false}
                      dropdownStyle={{ minWidth: 200 }}
                      tagRender={({ label, value, closable, onClose }) => {
                        const color = urgencyHexColors[value] || '#94a3b8';
                        return (
                          <span
                            style={{
                              background: '#fff',
                              color: color,
                              border: `1px solid ${color}`,
                              borderRadius: 4,
                              padding: '0 7px',
                              marginRight: 4,
                              fontSize: 12,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              lineHeight: '22px',
                            }}
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: color,
                                flexShrink: 0,
                              }}
                            />
                            {label}
                            {closable && (
                              <span
                                onClick={onClose}
                                style={{ cursor: 'pointer', marginLeft: 2 }}
                              >
                                ×
                              </span>
                            )}
                          </span>
                        );
                      }}
                    >
                      {Object.keys(urgencyTypes).map((urgency) => {
                        const urgencyValue = urgencyTypes[urgency];
                        const color = urgencyHexColors[urgencyValue] || '#94a3b8';
                        return (
                          <Option key={urgency} value={urgencyValue} label={urgency}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: color,
                                  flexShrink: 0,
                                }}
                              />
                              {urgency}
                            </span>
                          </Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>
              </div>

              {/* Organization Group (Platform only) */}
              {!isRestrictedTaskLevel(level) && (
                <div className="task-filters__group">
                  <span className="task-filters__group-label">Organization</span>
                  <div className="task-filters__group-items">
                    <div className="task-filters__group-item">
                      {renderFilterSelect({
                        placeholder: 'Adv. Type',
                        value: searchParams.advertiserTypes ? searchParams.advertiserTypes.split(',') : [],
                        onChange: handleAdvertiserTypeFilter,
                        options: Object.keys(advertiserTypes).map((type) => (
                          <Option key={type} value={advertiserTypes[type]} label={type}>
                            {type}
                          </Option>
                        )),
                      })}
                    </div>
                    {/* Channel Partner is a PLATFORM-only view of the tenant tree */}
                    {isPlatform && (
                      <div className="task-filters__group-item">
                        {renderFilterSelect({
                          placeholder: 'Channel Partner',
                          value: searchParams.channelPartner ? searchParams.channelPartner.split(',') : [],
                          onChange: handleChannelPartnerFilter,
                          loading: channelPartnerLoading,
                          showSearch: true,
                          options: channelPartnerOptions.map((org) => (
                            <Option key={org.id} value={org.id} label={org.name}>
                              {org.name}
                            </Option>
                          )),
                        })}
                      </div>
                    )}
                    <div className="task-filters__group-item">
                      {renderFilterSelect({
                        placeholder: 'Advertiser',
                        value: searchParams.advertiser ? searchParams.advertiser.split(',') : [],
                        onChange: handleAdvertiserFilter,
                        loading: advertiserLoading,
                        // PLATFORM's list comes from the local org tree and is already
                        // complete, so it filters client-side like the form's cascader.
                        onSearch: isPlatform ? undefined : fetchAdvertiserOrgs,
                        showSearch: true,
                        options: advertiserOptions.map((org) => (
                          <Option key={org.id} value={org.id} label={org.name}>
                            {org.name}
                          </Option>
                        )),
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Options Group */}
              <div className="task-filters__group task-filters__group--options">
                <div className="task-filters__options-row">
                  {!isKanban && (
                    <Checkbox
                      className="task-filters__checkbox"
                      checked={searchParams.showAllStatuses === true || searchParams.showAllStatuses === 'true'}
                      onChange={(e) => {
                        setSearchParams({ ...searchParams, showAllStatuses: e.target.checked });
                        onPaginationReset();
                      }}
                    >
                      Show all statuses
                    </Checkbox>
                  )}

                  {showMyTasks && (
                    <>
                      <Checkbox
                        className="task-filters__checkbox"
                        checked={visibleColumns.includes('following')}
                        onChange={(e) => {
                          const newColumns = e.target.checked
                            ? [...visibleColumns, 'following']
                            : visibleColumns.filter((c) => c !== 'following');
                          setVisibleColumns(newColumns);
                          setSearchParams({ ...searchParams, following: e.target.checked });
                        }}
                      >
                        Following
                      </Checkbox>
                      {level === 'PLATFORM' && (
                        <Checkbox
                          className="task-filters__checkbox"
                          checked={visibleColumns.includes('collaborating')}
                          onChange={(e) => {
                            const newColumns = e.target.checked
                              ? [...visibleColumns, 'collaborating']
                              : visibleColumns.filter((c) => c !== 'collaborating');
                            setVisibleColumns(newColumns);
                            setSearchParams({ ...searchParams, collaborating: e.target.checked });
                          }}
                        >
                          Collaborating
                        </Checkbox>
                      )}
                    </>
                  )}

                  {level === 'PLATFORM' && (
                    <div className="task-filters__project-toggle">
                      <span>Projects:</span>
                      <Checkbox
                        checked={projectFilter === 'projectOnly'}
                        onChange={(e) => {
                          setProjectFilter(e.target.checked ? 'projectOnly' : 'all');
                          onPaginationReset();
                        }}
                      >
                        Only
                      </Checkbox>
                      <Checkbox
                        checked={projectFilter === 'noProject'}
                        onChange={(e) => {
                          setProjectFilter(e.target.checked ? 'noProject' : 'all');
                          onPaginationReset();
                        }}
                      >
                        Exclude
                      </Checkbox>
                    </div>
                  )}

                  <div className="task-filters__preset-actions">
                    <Button
                      className="task-filters__preset-btn"
                      icon={<CloudDownloadOutlined />}
                      onClick={onLoadFilters}
                    >
                      Load Filters
                    </Button>
                    <Button
                      className="task-filters__preset-btn task-filters__preset-btn--save"
                      icon={<SaveOutlined />}
                      onClick={onSaveFilters}
                      type="primary"
                    >
                      Save Filters
                    </Button>
                  </div>
                </div>
              </div>
            </div>
        </div>
      )}

    </div>
  );
};

TaskBoardFilters.propTypes = {
  windowWidth: PropTypes.number.isRequired,
  showFilters: PropTypes.bool.isRequired,
  setShowFilters: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  setSearchParams: PropTypes.func.isRequired,
  level: PropTypes.string,
  showMyTasks: PropTypes.bool,
  isKanban: PropTypes.bool,
  // Filter handlers
  handleAssigneeFilter: PropTypes.func.isRequired,
  handleTeamsFilter: PropTypes.func.isRequired,
  handleRequestorFilter: PropTypes.func.isRequired,
  handleTaskTypeFilter: PropTypes.func.isRequired,
  handleTaskSubTypeFilter: PropTypes.func.isRequired,
  handleStatusFilter: PropTypes.func.isRequired,
  handleAdvertiserTypeFilter: PropTypes.func.isRequired,
  handleChannelPartnerFilter: PropTypes.func.isRequired,
  handleAdvertiserFilter: PropTypes.func.isRequired,
  handleUrgencyFilter: PropTypes.func.isRequired,
  handleDueDateFilter: PropTypes.func.isRequired,
  // User/Org data
  uniqueUsers: PropTypes.array.isRequired,
  uniqueTeams: PropTypes.array,
  uniqueRequestors: PropTypes.array.isRequired,
  channelPartnerOptions: PropTypes.array,
  advertiserOptions: PropTypes.array.isRequired,
  // Loading states
  basicUsersDetailsLoading: PropTypes.bool,
  teamsLoading: PropTypes.bool,
  requestorLoading: PropTypes.bool,
  channelPartnerLoading: PropTypes.bool,
  advertiserLoading: PropTypes.bool,
  // Fetch functions
  fetchBasicUsers: PropTypes.func.isRequired,
  fetchBasicUsersRequestor: PropTypes.func.isRequired,
  fetchAdvertiserOrgs: PropTypes.func.isRequired,
  // Filter actions
  visibleColumns: PropTypes.array.isRequired,
  setVisibleColumns: PropTypes.func.isRequired,
  projectFilter: PropTypes.string,
  setProjectFilter: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onSaveFilters: PropTypes.func.isRequired,
  onLoadFilters: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool,
  onPaginationReset: PropTypes.func.isRequired,
};

TaskBoardFilters.defaultProps = {
  level: null,
  showMyTasks: false,
  isKanban: false,
  uniqueTeams: [],
  channelPartnerOptions: [],
  basicUsersDetailsLoading: false,
  teamsLoading: false,
  requestorLoading: false,
  channelPartnerLoading: false,
  advertiserLoading: false,
  projectFilter: 'all',
  hasActiveFilters: false,
};

export default TaskBoardFilters;
